import { useEffect, useState } from 'react'

import { GameId, STORE_CAMPAIGN_TAG, STORE_CAMPAIGN_TAG_VALID_UNTIL_RE, StoreOfferProduct } from '@common'
import cx from 'classnames'
import Image from 'next/image'
import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'

import MiniEventPass from '@/assets/images/campaigns/mini-event-pass.png'
import { useBreakpoints } from '@/utils/useBreakpoints'

import { snowplowTrackCampaign } from '../../../../utils/snowplow'
import { getTimeRemaining, TimeLeft } from '../../../../utils/timer'
import { useGameSlug } from '../../game-provider'
import { BuyButton } from '../BuyButton/BuyButton'
import { CardBase } from '../card/CardBase'
import { useHandleClick } from '../card/useHandleClick'
import { rtlLocales } from '../constants'
import { TimeRemaining } from '../timeRemaining/TimeRemaining'
import { StoreTypography } from '../typography/StoreTypography'

import styles from './Campaign.module.scss'
import storeStyles from '../storefronts.module.scss'

interface CampaignProps {
  product: StoreOfferProduct
  enableNewVisuals?: GameId[]
}

/**
 * @param rawChronosTagTimestamp - Whole Store campaign tag content from Cronos (eg. store:campaign:2024-11-01T08:00Z)
 */
const useTimeremainingCampaign = (rawChronosTagTimestamp?: string) => {
  const [countdownExpired, setCountdownExpired] = useState(false)
  const [timeLeftText, setTimeLeftText] = useState<TimeLeft>()

  useEffect(() => {
    const tagParts = rawChronosTagTimestamp?.split(':') ?? []

    if (tagParts.length === 4) {
      const match = rawChronosTagTimestamp?.match(STORE_CAMPAIGN_TAG_VALID_UNTIL_RE)
      if (match) {
        const validUntil = new Date(match[1]).getTime()
        validUntil < Date.now() ? setCountdownExpired(true) : setCountdownExpired(false)
        const timeLeft = getTimeRemaining(new Date(match[1]).getTime())
        setTimeLeftText(timeLeft)
      }
    }
  }, [rawChronosTagTimestamp])

  return { timeLeftText, countdownExpired }
}

const CampaignProduct = () => {
  return (
    <CardBase className={cx(styles.campaignProduct, styles['clashofclans'])}>
      <div className={styles.header}>
        <Image
          className={styles.headerImage}
          src={MiniEventPass}
          alt="campaign-item-background"
          fill
          sizes="(max-width: 600px) 400px, 600px"
          quality={100}
          style={{ objectFit: 'contain' }}
        />
      </div>
    </CardBase>
  )
}

export const Campaign = ({ product, enableNewVisuals }: CampaignProps) => {
  const { from } = useBreakpoints()
  const t = useTranslations()
  const isDesktop = from('tablet')
  const gameSlug = useGameSlug()
  const pathname = `/${gameSlug}/product/${product.slug}`
  const { handleClick } = useHandleClick({ product, pathname, enableNewVisuals })
  const campaignTag = product.tags?.filter((tag) => tag.includes(STORE_CAMPAIGN_TAG))[0]
  const { timeLeftText, countdownExpired } = useTimeremainingCampaign(campaignTag)
  const locale = useLocale()

  const typography = {
    tag: 'p',
    style: 'plain',
    textSize: isDesktop ? 'md' : 'body',
    overrideFont: 'supercellText',
    lineHeight: 'normal',
  } as const

  return (
    <div className={cx(styles.campaign, storeStyles.topOverlap, storeStyles.bottomOverlap)}>
      <div className={styles.campaigHeading}>
        <div className={styles.title}>
          <StoreTypography className={styles.titleText} tag="h1" textSize={isDesktop ? 'xl' : 'lg'} style="outlined">
            {'New Mini Event Pass!'}
          </StoreTypography>
        </div>
        <div className={styles.timerContainer}>
          {!countdownExpired && timeLeftText && (
            <TimeRemaining
              timeLeftText={timeLeftText}
              prefixText={'Event ends in'}
              textSize="body"
              type={rtlLocales.includes(locale) ? 'primary' : 'secondary'}
              textStyle={rtlLocales.includes(locale) ? 'plain' : 'outlined'}
              variant="campaign"
            />
          )}
        </div>
      </div>
      <div className={styles.productContainer}>
        <CampaignProduct key={product.id} />
      </div>
      <div className={styles.description}>
        <StoreTypography className={styles.titleText} {...typography}>
          {
            'Dive into the Mini Spotlight Event from Sep. 9 - 20 with the Mini Event Pass and get more resources and Super Medals as you play!'
          }
        </StoreTypography>
        <div className={styles.ctaLinkContainer}>
          <div className={styles.campaignBuyButton}>
            <BuyButton product={product} onAddToCart={() => snowplowTrackCampaign('miniEventPass')} />
          </div>
          <div className={styles.readMore}>
            <Link className={styles.link} href={pathname} onClick={handleClick}>
              <StoreTypography className={styles.titleText} {...typography} textSize={isDesktop ? 'body' : 'sm'}>
                {`${t('lan_hero_button3')} ›`}
              </StoreTypography>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
